import { ProductCategory } from './product-category.class';
import { ProductItem } from './product-item.class';
import { Product } from './product.class';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { ProductItemGroup } from '@shared/classes/product-item-group.class';
import { ProductOrCategoryType } from '@shared/types/product-or-category.type';

export class ProductList {
  private readonly rootCategory = 'root';
  private openCategories: string[] = [];
  private productCategories: Map<string, ProductItem[]> = new Map<string, ProductItem[]>();
  private products: Map<string, ProductItem[]> = new Map<string, ProductItem[]>();
  private typeMap: Map<string, Map<string, ProductItem[]>> = new Map<string, Map<string, ProductItem[]>>();

  constructor() {
    this.productCategories.set(this.rootCategory, []);
    this.products.set(this.rootCategory, []);
    this.openCategories.push(this.rootCategory);
    this.typeMap.set(UPDATES_TYPES.Product.type, this.products);
    this.typeMap.set(UPDATES_TYPES.ProductCategory.type, this.productCategories);
  }

  add(type: string, products: ProductOrCategoryType[], categoryUuids?: string[]): void {
    const categoryUuidsFiltered: string[] = categoryUuids?.filter((uuid: string): boolean => Boolean(uuid));
    const uuids: string[] = (categoryUuidsFiltered && categoryUuidsFiltered.length) ? [...categoryUuidsFiltered] : [this.rootCategory];

    for (const categoryUuid of uuids) {
      const productData: Map<string, ProductItem[]> = this.typeMap.get(type);
      const productItems: ProductItem[] = productData.get(categoryUuid) || [];
      const activeProducts: ProductOrCategoryType[] = products.filter((product: ProductOrCategoryType): boolean => !product.deleted);
      if (!activeProducts.length) {
        return;
      }
      const isCategory: boolean = this.isCategory(type);
      const newProductItems: ProductItem[] = [
        ...productItems,
        ...activeProducts.map((product: ProductOrCategoryType): ProductItem => new ProductItem(product, categoryUuid, isCategory)),
      ];
      this.sortData(newProductItems);
      productData.set(categoryUuid, newProductItems);
    }
  }

  update(type: string, product: ProductOrCategoryType, categoryUuids?: string[]): void {
    const uuids: string[] = categoryUuids ? [...categoryUuids] : [this.rootCategory];
    const oldUuidsCategories: string[] = this.getOldCategories(product as Product);

    for (const categoryUuid of uuids) {
      const productData: Map<string, ProductItem[]> = this.typeMap.get(type);
      let productItems: ProductItem[] = productData.get(categoryUuid);

      if (!categoryUuid && !productItems) {
        productData.forEach((products: ProductItem[]): void => {
          const index = products.findIndex((productItem: ProductItem): boolean => productItem.data.uuid === product.uuid);
          if (index > -1) {
            productItems = products;
          }
        });
      }

      if (productItems) {
        const index: number = productItems.findIndex((productItem: ProductItem): boolean => productItem.data.uuid === product.uuid);
        if (index > -1) {
          if (!product.visible || product.deleted) {
            if (type === UPDATES_TYPES.ProductCategory.type) {
              this.hideProductsAndCategory(product.uuid);
            }
            productItems.splice(index, 1);
            productData.set(categoryUuid, productItems);
          } else {
            productItems[index].data = product;
          }
        }
      }
    }

    this.normalizeProductInCategories(product as Product, oldUuidsCategories, uuids);
  }

  has(type: string, uuid: string): boolean {
    const productData: Map<string, ProductItem[]> = this.typeMap.get(type);
    return !!this.findByUuid(productData, uuid);
  }

  find(type: string, uuid: string): ProductItem {
    const productData: Map<string, ProductItem[]> = this.typeMap.get(type);
    return this.findByUuid(productData, uuid);
  }

  private findByUuid(data: Map<string, ProductItem[]>, uuid: string): ProductItem {
    const values = data.values();
    for (let i = 0; i < data.size; i++) {
      const productItems: ProductItem[] = values.next().value;
      const result: ProductItem = productItems.find((productItem: ProductItem): boolean => productItem.uuid === uuid);
      if (result) {
        return result;
      }
    }
    return null;
  }

  addProducts(products: Product[], categoryUuid?: string): void {
    categoryUuid = this.getCategoryUuid(categoryUuid);
    const saveProductItems: ProductItem[] = this.products.get(categoryUuid) || [];
    const productItems: ProductItem[] = products.map((p: Product) => new ProductItem(p, categoryUuid, false));

    // Performance optimization: Use Map for O(N) deduplication instead of O(N^2)
    const uniqueMap: Map<string, ProductItem> = new Map<string, ProductItem>();

    // Add existing items to map
    for (const item of saveProductItems) {
      uniqueMap.set(item.uuid, item);
    }

    // Add new items (overwriting if duplicates exist, which prioritizes newer data)
    for (const item of productItems) {
      if (!uniqueMap.has(item.uuid)) {
        uniqueMap.set(item.uuid, item);
      }
    }

    this.products.set(categoryUuid, Array.from(uniqueMap.values()));
  }

  addProductCategories(productCategories: ProductCategory[], categoryUuid?: string): void {
    categoryUuid = this.getCategoryUuid(categoryUuid);
    const productCategoriesItems: ProductItem[] = productCategories.map(
      (p: ProductCategory): ProductItem => new ProductItem(p, categoryUuid, true)
    );
    this.productCategories.set(categoryUuid, productCategoriesItems);
  }

  hideProductsAndCategory(categoryUuid: string): void {
    const index: number = this.openCategories.findIndex((category: string): boolean => category === categoryUuid);
    if (index === 0) {
      this.openCategories = this.openCategories.slice(0, 1);
    } else if (index > 0) {
      const closeOpenCategory = this.openCategories.splice(index);
      closeOpenCategory.forEach((uuid) => {
        this.deleteDataByKey(this.productCategories, uuid);
        this.deleteDataByKey(this.products, uuid);
      });
    }
  }

  closeCategory(uuid: string): void {
    uuid = this.getCategoryUuid(uuid);
    const openCategoryUuid: string = this.getOpenCategoryUuid(uuid);
    if (openCategoryUuid) {
      this.hideProductsAndCategory(openCategoryUuid);
    }
  }

  private getOpenCategoryUuid(uuid: string): string {
    const values = this.productCategories.values();
    for (let i = 0; i < this.productCategories.size; i++) {
      const productItems: ProductItem[] = values.next().value;
      const hasCategoryUuid: boolean = productItems.some((productItem: ProductItem): boolean => productItem.uuid === uuid);
      const hasOpenCategory: boolean = productItems.some((productItem: ProductItem): boolean => productItem.expanded);
      if (hasCategoryUuid && hasOpenCategory) {
        productItems.forEach((productItem) => (productItem.expanded = false));
        return this.openCategories[i + 1];
      }
    }
  }

  openCategory(categoryUuid: string): void {
    const hasCategories: boolean = this.hasOpenCategory(categoryUuid);
    if (!hasCategories) {
      this.openCategories.push(categoryUuid);
    }
  }

  hasOpenCategory(categoryUuid: string): boolean {
    categoryUuid = this.getCategoryUuid(categoryUuid);
    return this.openCategories.some((category) => category === categoryUuid);
  }

  private deleteDataByKey(data: Map<string, ProductItem[]>, categoryUuid: string): void {
    const hasCategoryKey: boolean = data.has(categoryUuid);
    if (hasCategoryKey) {
      data.delete(categoryUuid);
    }
  }

  private isCategory(type: string): boolean {
    return type === UPDATES_TYPES.ProductCategory.type;
  }

  getProductItemGroups(size: number): ProductItemGroup[] {
    return this.openCategories.reduce((result, category, currentIndex) => {
      const oneProductItemGroup: ProductItemGroup[] = this.getOneProductItemGroup(category, size, currentIndex);
      if (category !== this.rootCategory) {
        const categoryIndex: number = result.findIndex((productItemGroup: ProductItemGroup): boolean => {
          for (let index = 0; index < productItemGroup.productItemList.length; index++) {
            const element: ProductItem = productItemGroup.productItemList[index];
            if (element && element.uuid === category) {
              return true;
            }
          }
          return false;
        });
        if (categoryIndex > -1) {
          result.splice(categoryIndex + 1, 0, ...oneProductItemGroup);
          return result;
        }
      }
      return [...result, ...oneProductItemGroup];
    }, []);
  }

  private sliceGroups(data: any[], size: number, order: number): ProductItemGroup[] {
    const result = [];
    if (data) {
      for (let i = 0; i < Math.ceil(data.length / size); i++) {
        result[i] = new ProductItemGroup(data.slice(i * size, i * size + size), order);
      }
    }
    return result;
  }

  private getOneProductItemGroup(categoryUuid: string, size: number, order: number): ProductItemGroup[] {
    const productCategories: ProductItem[] = this.productCategories.get(categoryUuid);
    const sliceProductCategory: ProductItemGroup[] = this.sliceGroups(productCategories, size, order);
    const products: ProductItem[] = this.products.get(categoryUuid);
    const sliceProducts: ProductItemGroup[] = this.sliceGroups(products, size, order);
    return [...sliceProductCategory, ...sliceProducts];
  }

  private sortData(data: ProductItem[]): void {
    data.sort((a: ProductItem, b: ProductItem) => {
      const nameA: string = a.data?.name?.toLowerCase();
      const nameB: string = b.data?.name?.toLowerCase();
      return nameA > nameB ? 1 : nameB > nameA ? -1 : 0;
    });
  }

  private getCategoryUuid(categoryUuid: string): string {
    return categoryUuid || this.rootCategory;
  }

  private getOldCategories(productUuid: Product): string[] {
    const result: string[] = [];
    this.products.forEach((productItems: ProductItem[], categoryUuid: string): void => {
      const productItem: boolean = Boolean(productItems.find((item: ProductItem): boolean => item.data.uuid === productUuid.uuid));
      if (productItem) {
        result.push(categoryUuid);
      }
    });
    return [...result];
  }

  private normalizeProductInCategories(product: Product, old: string[], uuids: string[]): void {
    if (!Boolean(this.products) || !old || !old.length) {
      return;
    }

    const needToRemove: string[] = old.filter((item: string): boolean => !uuids.includes(item));
    const needToAdd: string[] = uuids.filter((item: string): boolean => !old.includes(item));

    if (needToRemove && needToRemove?.length > 0) {
      for (const uuid of needToRemove) {
        this.products.set(
          uuid,
          this.products.get(uuid).filter((element: ProductItem): boolean => product.uuid !== element.data.uuid)
        );
      }
    }

    if (needToAdd && needToAdd?.length > 0) {
      this.add(UPDATES_TYPES.Product.type, [product], needToAdd);
    }
  }
}
